/**
 * Every View and Print shows its PDF, image, printout or print page here.
 * Mounted once at the app root; lib/documentViewer.ts holds what is on screen.
 * Above any open box so a document opened from one is seen over it; only the
 * refusal box sits higher.
 *
 * A printout's HTML runs with scripts off (sandbox without allow-scripts), so
 * nothing in it can print itself or act as the application; allow-modals lets
 * the Print button open the print dialog for the frame alone. A print page is
 * this app's own route and runs as itself; Print waits for its ready mark.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from './Dialog';
import {
  closeShownDocument,
  subscribeToShownDocument,
  type ShownDocument,
} from '../lib/documentViewer';

const FRAME_STYLE = { display: 'block', width: '100%', height: 'calc(100vh - 220px)', border: 'none' };

export function DocumentViewer(): JSX.Element | null {
  const { t } = useTranslation('common');
  const [doc, setDoc] = useState<ShownDocument | null>(null);
  const [pageReady, setPageReady] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => subscribeToShownDocument(setDoc), []);

  useEffect(() => {
    setPageReady(false);
    if (doc?.page === undefined) return;
    const timer = window.setInterval(() => {
      if (frame.current?.contentDocument?.querySelector('[data-print-ready="true"]')) {
        setPageReady(true);
        window.clearInterval(timer);
      }
    }, 200);
    return () => window.clearInterval(timer);
  }, [doc]);

  if (!doc) return null;

  const print = (): void => {
    frame.current?.contentWindow?.print();
  };

  const printable = doc.html !== undefined || doc.page !== undefined;

  return (
    <Dialog
      title={doc.title}
      onClose={closeShownDocument}
      testId="document-viewer"
      width={1100}
      zIndex={9999}
      footer={
        printable ? (
          <button
            type="button"
            className="btn pri sm"
            onClick={print}
            disabled={doc.page !== undefined && !pageReady}
            data-testid="document-viewer-print"
          >
            {t('buttons.print')}
          </button>
        ) : undefined
      }
    >
      {doc.html !== undefined ? (
        <iframe
          ref={frame}
          title={doc.title}
          srcDoc={doc.html}
          sandbox="allow-same-origin allow-modals"
          // A printout is a sheet of paper: white whatever the app's theme.
          style={{ ...FRAME_STYLE, background: 'white' }}
          data-testid="document-viewer-frame"
        />
      ) : doc.page !== undefined ? (
        <iframe ref={frame} title={doc.title} src={doc.page} style={FRAME_STYLE} data-testid="document-viewer-frame" />
      ) : (
        <iframe title={doc.title} src={doc.url} style={FRAME_STYLE} data-testid="document-viewer-frame" />
      )}
    </Dialog>
  );
}
